export interface SearchOrigin { lat: number; lng: number }
export interface LocationProvider { getCurrentPosition(): Promise<SearchOrigin> }

export const LOCATION_WATCHDOG_MS = 5_000;

export function roundOrigin(origin: SearchOrigin): SearchOrigin {
  if (!Number.isFinite(origin.lat) || !Number.isFinite(origin.lng) || Math.abs(origin.lat) > 90 || Math.abs(origin.lng) > 180) {
    throw new Error('invalid_origin');
  }
  return { lat: Number(origin.lat.toFixed(3)), lng: Number(origin.lng.toFixed(3)) };
}

/** PostgREST pode entregar geography como EWKB hexadecimal; onboarding grava POINT(lng lat). */
export function parseProfilePoint(raw: unknown): SearchOrigin | null {
  try {
    if (typeof raw === 'string') {
      const wkt = /^POINT\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)$/i.exec(raw);
      if (wkt) return roundOrigin({ lng: Number(wkt[1]), lat: Number(wkt[2]) });
      if (/^(?:[0-9a-f]{2}){21,}$/i.test(raw)) {
        const bytes = Uint8Array.from(raw.match(/.{2}/g)!.map((pair) => Number.parseInt(pair, 16)));
        const view = new DataView(bytes.buffer);
        const little = bytes[0] === 1;
        if (!little && bytes[0] !== 0) return null;
        const kind = view.getUint32(1, little);
        if ((kind & 0xff) !== 1 || (kind & 0x40000000) !== 0 || (kind & 0x80000000) !== 0) return null;
        const hasSrid = (kind & 0x20000000) !== 0;
        const start = hasSrid ? 9 : 5;
        if (bytes.length !== start + 16 || (hasSrid && view.getUint32(5, little) !== 4326)) return null;
        return roundOrigin({ lng: view.getFloat64(start, little), lat: view.getFloat64(start + 8, little) });
      }
      return null;
    }
    if (raw && typeof raw === 'object' && 'type' in raw && 'coordinates' in raw && raw.type === 'Point' && Array.isArray(raw.coordinates) && raw.coordinates.length === 2 && typeof raw.coordinates[0] === 'number' && typeof raw.coordinates[1] === 'number') {
      return roundOrigin({ lng: raw.coordinates[0], lat: raw.coordinates[1] });
    }
  } catch { return null; }
  return null;
}

export function webLocationProvider(): LocationProvider {
  return {
    getCurrentPosition: () => new Promise((resolve, reject) => {
      if (!navigator.geolocation) { reject(new Error('unavailable')); return; }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({ lat: position.coords.latitude, lng: position.coords.longitude }),
        (error) => reject(error),
        { enableHighAccuracy: false, maximumAge: 60_000, timeout: LOCATION_WATCHDOG_MS },
      );
    }),
  };
}

/** Solicita apenas a localização aproximada após o gesto do usuário. */
export function nativeLocationProvider(): LocationProvider {
  return {
    getCurrentPosition: async () => {
      const permission = await Geolocation.requestPermissions({ permissions: ['coarseLocation'] });
      if (permission.coarseLocation !== 'granted') throw { code: 1 };
      const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, maximumAge: 60_000 });
      return { lat: position.coords.latitude, lng: position.coords.longitude };
    },
  };
}

export function deviceLocationProvider(): LocationProvider {
  return Capacitor.isNativePlatform() ? nativeLocationProvider() : webLocationProvider();
}

/** Timer independente: no Android v6 a opção timeout do plugin é ignorada. */
export async function requestApproximatePosition(provider: LocationProvider, timeoutMs = LOCATION_WATCHDOG_MS): Promise<SearchOrigin> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const position = await Promise.race([
      provider.getCurrentPosition(),
      new Promise<never>((_resolve, reject) => { timer = setTimeout(() => reject(new Error('timeout')), timeoutMs); }),
    ]);
    return roundOrigin(position);
  } finally { if (timer) clearTimeout(timer); }
}
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
